import { App, BlockedResourceError, NotFoundError, UserVerificationPendingError, AppStatusError, AppStatus, PaymentPendingError, DeploymentStatusError, AuthorizationError } from "@back4app2/sdk";
import PreDeployConfirmModal from './PreDeployConfirmModal';
import { RunAssistance } from '@back4app2/sdk';
import { useState, useRef, useCallback, useEffect } from "react";
import back4app2 from "../back4app2";
import { ReactComponent as DownArrowIcon } from "../assets/images/down-arrow.svg";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { ReactComponent as StatusSuccessSVG } from '../assets/images/status-success.svg';
import { ReactComponent as StatusSpinnerSVG } from '../assets/images/status-spinner.svg';
import { AmplitudeEvent, trackEvent } from "../utils/amplitude";
import { hasWritePermission } from "../utils/permissions";
import { TOAST_SURFACE } from "./ds/toast";

/**
 * Runs the analysis, then decides what the Deploy button does.
 *
 * Wired only into the explicit Deploy actions, not into "save settings and
 * redeploy" or "domain changed, redeploy": someone who has just configured the
 * app does not need to be asked about its configuration, and a screen that
 * appears on every path is one nobody reads.
 */
const runPreDeploy = async (serviceEnvironmentId: string) => {
  try {
    return await back4app2.prepareDeployment(serviceEnvironmentId);
  } catch (e) {
    // Never block the deploy on the pre-check: it decides what to SHOW, and
    // the worker gate is what actually enforces. Failing here would turn an
    // analyzer problem into an inability to deploy.
    return { blocked: false, needsConfirmation: false };
  }
};

/**
 * Whether the menu has anything to offer.
 *
 * Exported because the component renders `null` when the answer is no, and a
 * caller that supplies its own trigger — the sidebar's status card — has to
 * know not to draw a chevron onto something that opens nothing.
 */
export const canRunAppActions = (app: App): boolean => {
  const env = app.mainService && app.mainService.mainServiceEnvironment;
  if (!env) {
    return false;
  }
  if ([AppStatus.INITIALIZING, AppStatus.PENDING_VERIFICATION, AppStatus.FAILED].includes(app.status)) {
    return false;
  }
  if (env.isPendingPayment || (!env.isFreePlanElegible && env.plan.name.includes('Free'))) {
    return false;
  }
  return hasWritePermission(app, 'Deploy');
};

const MyAppActions = ({ app, compact = false, variant = 'button', children }: {
  app: App,
  /** Inline in the mobile header, where a full-width 38px control was the
   * biggest thing on a screen whose subject is the container below it. */
  compact?: boolean,
  /**
   * `card` hands the trigger over to the caller: the sidebar's status card is
   * the thing you press, so the menu keeps the outside-click handling and the
   * pre-deploy modal but draws no button of its own.
   */
  variant?: 'button' | 'card',
  children?: React.ReactNode,
}) => {
  const mainServiceEnvironment = app.mainService && app.mainService.mainServiceEnvironment;
  const navigate = useNavigate();

  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pendingDeploy, setPendingDeploy] = useState<{
    serviceEnvironmentId: string;
    runAssistance: RunAssistance;
  } | null>(null);

  const handleClickOutsideEvent = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    },
    [],
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutsideEvent);
    document.addEventListener('touchstart', handleClickOutsideEvent);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideEvent);
      document.removeEventListener('touchstart', handleClickOutsideEvent);
    }
  }, [handleClickOutsideEvent]);

  const deployLatestCommit = async () => {
    setIsOpen(false);
    
    if (!hasWritePermission(app, 'Deploy')) {
      toast.error('You don\'t have permission to trigger deployments.', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      return;
    }

    trackEvent(AmplitudeEvent.DEPLOY_LATEST_COMMIT);
    const toastId = toast.loading('Deploying latest commit...', {
      className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
      icon: <StatusSpinnerSVG width="24px" height="24px" color="text-old-blue" className="animate-spin" />
    });
    try {
      const prepared = await runPreDeploy(
        mainServiceEnvironment?.id as string
      );

      if (prepared.blocked) {
        // The block card on the overview carries the explanation and the
        // appeal; a toast would only repeat it worse.
        toast.dismiss(toastId);
        navigate(`/apps/${app.id}`);
        return;
      }

      if (prepared.needsConfirmation && prepared.runAssistance) {
        toast.dismiss(toastId);
        setPendingDeploy({
          serviceEnvironmentId: mainServiceEnvironment?.id as string,
          runAssistance: prepared.runAssistance,
        });
        return;
      }

      await back4app2.triggerManualDeployment(mainServiceEnvironment?.id as string);
      toast.success('New deployment created!', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        id: toastId,
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in top-" />
      });
      navigate(`/apps/${app.id}`);
    } catch (err) {
      let errMsg = 'Something went wrong!';
      if (err instanceof AppStatusError || err instanceof UserVerificationPendingError || err instanceof BlockedResourceError || err instanceof PaymentPendingError || err instanceof NotFoundError || err instanceof DeploymentStatusError || err instanceof AuthorizationError) {
        errMsg = err.message;
      }
      toast.error(errMsg, {
        className: TOAST_SURFACE.replace('max-w-xs', 'min-w-xs'),
        id: toastId,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
    }
  }

  if (!canRunAppActions(app)) {
    return null;
  }

  // No layout of its own: the sidebar and the mobile header each place it.
  return <div className={`relative ${compact ? 'w-auto' : 'w-full'}`} ref={ref}>
    {variant === 'card' ? (
      <button
        className="b4-focus block w-full text-left rounded-card"
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="App actions"
        onClick={() => setIsOpen(prev => !prev)}
      >
        {children}
      </button>
    ) : (
      <button
        className={`b4-focus bg-navy-700 border border-navy-400 rounded-control hover:bg-navy-600 flex items-center justify-between font-medium text-navy-100 transition-colors duration-[120ms] ${
          compact ? 'h-8 px-2.5 gap-1.5 text-ds-125' : 'w-full h-[38px] px-3 text-[13px]'
        } ${isOpen ? 'rounded-b-none border-b-0' : ''}`}
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(prev => !prev)}
      >
        Action
        <DownArrowIcon width="12px" height="8px" className={`${compact ? '' : 'ml-2'} text-navy-300 transition-transform duration-[120ms] ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
      </button>
    )}
    {/* Compact opens right-aligned: it sits at the edge of the header, and a
        left-anchored panel would run off the screen. The card variant is a
        detached panel — the trigger keeps all four of its own corners. */}
    <div className={`${isOpen ? 'opacity-100' : 'opacity-0 hidden'} absolute top-full overflow-hidden bg-navy-700 text-[13px] z-[999] list-none border border-navy-400 shadow-[0_2px_4px_rgba(0,0,0,.28),0_8px_16px_rgba(0,0,0,.24)] ${
      variant === 'card'
        ? 'left-0 w-full mt-1 rounded-control'
        : compact ? 'right-0 w-max min-w-[220px] border-t-0 rounded-b-control rounded-bl-control' : 'left-0 w-full border-t-0 rounded-b-control'
    }`}>
      <ul className="py-1">
        <li className="hover:cursor-pointer text-navy-100 hover:bg-navy-600 hover:text-white transition-colors duration-[120ms] px-3 py-2.5" onClick={deployLatestCommit}>Deploy the latest commit</li>
        {/* <li className="hover:cursor-pointer hover:bg-[#34506F] transition-all duration-200 px-[0.625rem] py-[0.4375em]">Deploy a specific commit</li>
        <li className="hover:cursor-pointer hover:bg-[#34506F] transition-all duration-200 px-[0.625rem] py-[0.4375em]">SuspendApp/Resume App</li> */}
      </ul>
  </div>
  {pendingDeploy && app.mainService?.mainServiceEnvironment ? (
    <PreDeployConfirmModal
      appId={app.id}
      serviceEnvironment={app.mainService.mainServiceEnvironment}
      runAssistance={pendingDeploy.runAssistance}
      onCancel={() => setPendingDeploy(null)}
      onProceed={async () => {
        const { serviceEnvironmentId } = pendingDeploy;
        setPendingDeploy(null);
        await back4app2.triggerManualDeployment(serviceEnvironmentId);
        navigate(`/apps/${app.id}`);
      }}
    />
  ) : null}
</div>;
};

export default MyAppActions;
